import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import { GoogleTagManagerProvider } from "@/providers/GoogleTagManager/GoogleTagManager.provider";
import {
  isTruthyEnvValue,
  resolveSentryRuntimeConfig,
} from "@/utils/monitoring/sentryRuntimeConfig";

import "./i18n/i18n";
import "./index.css";
import { paths } from "./paths";
import { router } from "./router";

const environment = import.meta.env.VITE_ENV;
const gtmId = import.meta.env.VITE_GTM_ID;

async function enableMocking(enable: string | boolean | undefined) {
  if (!isTruthyEnvValue(enable)) {
    return;
  }

  // Belt & Braces: Make doubly sure that Mock Service Worker is never enable in Production
  if (import.meta.env.PROD) {
    return;
  }

  const { worker } = await import("./mocks/browser");

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start({
    serviceWorker: {
      url: `${paths.BASE_WEB_APP_PATH}/mockServiceWorker.js`,
      options: {
        scope: "/",
      },
    },
    onUnhandledRequest(request, print) {
      // Ignore any requests containing "api/" in their URL.
      if (!request.url.includes("api/")) {
        return;
      }

      // Otherwise, print an unhandled request warning.
      print.warning();
    },
  });
}

function enableSentry(
  environment: "development" | "staging" | "production",
  enabledFlag: string | boolean | undefined
) {
  const trimmedDsn = import.meta.env.VITE_SENTRY_DSN?.trim() || undefined;

  const sentryConfig = resolveSentryRuntimeConfig({
    environment,
    enabledFlag,
    dsn: trimmedDsn,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    replaysSessionSampleRate:
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate:
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    captureConsole: import.meta.env.VITE_SENTRY_CAPTURE_CONSOLE,
  });

  const integrations = sentryConfig.enabled ? [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.zodErrorsIntegration({
      saveZodIssuesAsAttachment: true,
    }),
  ]: [];

  if (sentryConfig.enabled && sentryConfig.captureConsole) {
    integrations.push(Sentry.captureConsoleIntegration());
  }

  if (!sentryConfig.enabled) {
    console.warn("Sentry disabled - errors will log to console");
  }

  try {
    Sentry.init({
      enabled: sentryConfig.enabled,
      release: sentryConfig.release,
      dsn: trimmedDsn,
      integrations,
      environment,
      tracesSampleRate: sentryConfig.tracesSampleRate,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        "https://apps.staging.twinkl.co.uk/curriculum-architect",
        "https://apps.twinkl.co.uk/curriculum-architect",
      ],
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
      ignoreTransactions: [/^\/curriculum-architect\/test$/],
      beforeSend(event, hint) {
        if (sentryConfig.enabled) {
          return event;
        }
        console.error(hint.originalException);
        return null;
      },
    });
  } catch (e) {
    console.warn("Unable to start Sentry", e);
  }
}

function bootApp() {
  return createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <GoogleTagManagerProvider gtmId={gtmId} />
      <RouterProvider router={router} />
    </StrictMode>
  );
}

enableSentry(environment, import.meta.env.VITE_ENABLE_SENTRY);

enableMocking(import.meta.env.VITE_ENABLE_MSW)
  .catch((e) => {
    if (!import.meta.env.PROD) {
      console.error("Error Enabling MSW", e);
    }
  })
  .finally(bootApp);
